import React, { useMemo, useState } from 'react';
import { Icons } from '../Icons';
import { Appointment, AppointmentStatus, Patient, Dentist } from '../../types';
import { APPT_STATUS } from '../../constants/appointmentStatus';

type LayoutMode = 'list' | 'grid' | 'board' | 'slots';

const STATUS_COLUMNS: AppointmentStatus[] = [
  'SCHEDULED',
  'CONFIRMED',
  'CHECKED_IN',
  'RESCHEDULED',
  'COMPLETED',
  'CANCELLED',
  'NO_SHOW',
  'REQUESTED',
];

interface AppointmentsProps {
  appointments: Appointment[];
  patients: Patient[];
  dentists: Dentist[];
  onEditAppointment: (appointment: Appointment) => void;
  onViewAppointment?: (appointment: Appointment) => void;
  onDeleteAppointment?: (id: string) => void;
  onCollectPayment?: (appointment: Appointment) => void;
  isPatient?: boolean;
}

export const AppointmentsView: React.FC<AppointmentsProps> = ({
  appointments,
  patients,
  dentists,
  onEditAppointment,
  onViewAppointment,
  onDeleteAppointment,
  onCollectPayment,
  isPatient = false,
}) => {
  const [search, setSearch] = useState('');
  const [layout, setLayout] = useState<LayoutMode>('board');
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [slotDate, setSlotDate] = useState(() => new Date().toISOString().split('T')[0]);

  const layoutOptions: { value: LayoutMode; label: string }[] = [
    { value: 'board', label: 'Status board' },
    { value: 'grid', label: 'Grid' },
    { value: 'list', label: 'List' },
    { value: 'slots', label: 'Slots' },
  ];

  const filteredAppointments = useMemo(() => {
    if (!search.trim()) return appointments;
    const q = search.toLowerCase();
    return appointments.filter(appt => {
      const patient = patients.find(p => p.id === appt.patientId);
      const dentist = dentists.find(d => d.id === appt.providerId);
      const haystack = `${patient?.name || ''} ${appt.type} ${dentist?.name || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [appointments, patients, dentists, search]);

  const totalPages = Math.max(1, Math.ceil(filteredAppointments.length / pageSize));
  const pagedAppointments = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredAppointments.slice(start, start + pageSize);
  }, [filteredAppointments, page]);

  const statusBuckets = useMemo(
    () =>
      STATUS_COLUMNS.map(status => ({
        status,
        label: APPT_STATUS[status].label,
        color: APPT_STATUS[status].color,
        icon: APPT_STATUS[status].icon as keyof typeof Icons,
        items: filteredAppointments
          .filter(a => a.status === status)
          .sort((a, b) => `${a.date}T${a.time}`.localeCompare(`${b.date}T${b.time}`)),
      })),
    [filteredAppointments]
  );

  const ActionButton = ({
    label,
    onClick,
    tone = 'slate',
    icon: IconComponent,
    disabled,
  }: {
    label: string;
    onClick: () => void;
    tone?: 'slate' | 'blue' | 'emerald' | 'red';
    icon: keyof typeof Icons;
    disabled?: boolean;
  }) => {
    const base = 'h-9 w-9 flex items-center justify-center rounded-lg border text-xs font-bold transition-all';
    const toneClass =
      tone === 'blue'
        ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
        : tone === 'emerald'
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
          : tone === 'red'
            ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
            : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100';
    const IconEl = Icons[IconComponent];
    return (
      <button
        type="button"
        className={`${base} ${toneClass} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={onClick}
        title={label}
        aria-label={label}
        disabled={disabled}
      >
        <IconEl />
      </button>
    );
  };

  const renderActions = (appt: Appointment, wrapperClass = 'mt-3 flex gap-2 flex-wrap') => (
    <div className={wrapperClass}>
      <ActionButton label="View details" onClick={() => onViewAppointment?.(appt)} icon="FileText" />
      <ActionButton
        label={isPatient ? 'Request reschedule' : 'Reschedule'}
        onClick={() => onEditAppointment(appt)}
        icon="Edit"
        tone="blue"
      />
      {!isPatient && onCollectPayment && (
        <ActionButton
          label="Collect payment"
          onClick={() => onCollectPayment(appt)}
          icon="DollarSign"
          tone="emerald"
        />
      )}
      {!isPatient && onDeleteAppointment && (
        <ActionButton
          label="Delete appointment"
          onClick={() => onDeleteAppointment(appt.id)}
          icon="Trash2"
          tone="red"
        />
      )}
    </div>
  );

  const renderCard = (appt: Appointment) => {
    const patient = patients.find(p => p.id === appt.patientId);
    const dentist = dentists.find(d => d.id === appt.providerId);
    const status = APPT_STATUS[appt.status];
    const StatusIcon = Icons[status.icon as keyof typeof Icons];
    return (
      <div
        key={appt.id}
        className="p-6 bg-slate-50 rounded-2xl border border-slate-100 hover:bg-slate-100 transition-all"
      >
        <div className="flex items-center justify-between mb-3">
          <div className={`flex items-center space-x-2 px-3 py-1 rounded-xl text-[11px] font-black ${status.color}`}>
            <StatusIcon />
            <span>{status.label}</span>
          </div>
          <span className="text-xs text-slate-500">{appt.date} - {appt.time}</span>
        </div>
        <p className="font-bold text-lg truncate">{patient?.name || 'Unknown'}</p>
        <p className="text-sm text-slate-500 truncate">{appt.type} - {dentist?.name}</p>
        <p className="text-xs text-slate-400 mt-2">Room: {appt.room} - Duration: {appt.duration} min</p>
        {renderActions(appt)}
      </div>
    );
  };

  const renderBoardCard = (appt: Appointment) => {
    const patient = patients.find(p => p.id === appt.patientId);
    const dentist = dentists.find(d => d.id === appt.providerId);
    const status = APPT_STATUS[appt.status];
    return (
      <div
        key={appt.id}
        className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 space-y-2"
      >
        <div className="flex items-center justify-between gap-2">
          <p className="font-bold text-slate-800 truncate">{patient?.name || 'Unknown'}</p>
          <span className="text-[11px] font-bold text-slate-500">{appt.time}</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-slate-600">
          <span className={`px-2 py-1 rounded-lg font-black ${status.color}`}>{status.label}</span>
          <span className="font-bold text-slate-500">{appt.date}</span>
        </div>
        <p className="text-xs text-slate-500 truncate">{appt.type} with {dentist?.name}</p>
        <p className="text-[11px] text-slate-400">Room {appt.room} - {appt.duration} min</p>
        {renderActions(appt)}
      </div>
    );
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h2 className="text-3xl font-black">My Appointments</h2>
        <div className="flex bg-slate-100 rounded-xl p-1 self-start">
          {layoutOptions.map(option => (
            <button
              key={option.value}
              onClick={() => setLayout(option.value)}
              className={`px-3 py-2 rounded-lg text-xs font-bold capitalize transition-all ${
                layout === option.value ? 'bg-white shadow-sm' : 'text-slate-500'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-3xl border shadow-sm overflow-hidden animate-scaleIn">
        <div className="p-8 space-y-6">
          <div className="relative max-w-xl">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
              <Icons.Search />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by patient, type, or dentist"
              className="w-full pl-12 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm"
            />
          </div>
          {filteredAppointments.length === 0 ? (
            <div className="text-center py-12 text-slate-300">
              <p className="font-bold">No appointments scheduled</p>
              <p className="text-sm mt-2">Click "Book Appointment" to create one</p>
            </div>
          ) : (
            <>
              {layout === 'list' ? (
                <div className="space-y-4">
                  {pagedAppointments.map(appt => {
                    const patient = patients.find(p => p.id === appt.patientId);
                    const dentist = dentists.find(d => d.id === appt.providerId);
                    const status = APPT_STATUS[appt.status];
                    const StatusIcon = Icons[status.icon as keyof typeof Icons];

                    return (
                      <div
                        key={appt.id}
                        className="p-6 bg-slate-50 rounded-2xl flex items-center justify-between hover:bg-slate-100"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="p-3 bg-white rounded-xl">
                            <Icons.Clock />
                          </div>
                          <div>
                            <p className="font-bold text-lg">{patient?.name || 'Unknown'}</p>
                            <p className="text-sm text-slate-500">
                              {appt.date} at {appt.time} - {appt.type} - {dentist?.name}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          <div className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-[11px] font-black ${status.color}`}>
                            <StatusIcon />
                            <span>{status.label}</span>
                          </div>
                          {renderActions(appt, 'flex gap-2 flex-wrap justify-end')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : layout === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {pagedAppointments.map(renderCard)}
                </div>
              ) : layout === 'board' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                    {statusBuckets.map(bucket => {
                      const StatusIcon = Icons[bucket.icon as keyof typeof Icons];
                      return (
                        <div
                          key={bucket.status}
                          className="p-4 bg-slate-50 rounded-2xl border border-slate-200 flex flex-col gap-3"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className={`px-3 py-1 rounded-xl text-[11px] font-black ${bucket.color}`}>
                                {bucket.label}
                              </span>
                              <span className="px-2 py-1 rounded-lg bg-white border text-[11px] font-bold">
                                {bucket.items.length} {bucket.items.length === 1 ? 'appt' : 'appts'}
                              </span>
                            </div>
                            <StatusIcon />
                          </div>
                          {bucket.items.length === 0 ? (
                            <p className="text-xs text-slate-400">No appointments in this status.</p>
                          ) : (
                            <div className="space-y-3">
                              {bucket.items.map(renderBoardCard)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                    <label className="text-sm font-bold text-slate-600">Select date</label>
                    <input
                      type="date"
                      value={slotDate}
                      onChange={(e) => setSlotDate(e.target.value)}
                      className="border rounded-xl p-2 text-sm w-48"
                    />
                  </div>
                  <div className="space-y-2">
                    {Array.from({ length: 11 }, (_, i) => 8 + i).map(hour => {
                      const timeLabel = `${hour.toString().padStart(2, '0')}:00`;
                      const slotItems = filteredAppointments.filter(
                        a => a.date === slotDate && a.time.startsWith(timeLabel.slice(0, 2))
                      );
                      return (
                        <div key={hour} className="border rounded-2xl p-3 bg-slate-50">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-bold text-slate-700">{timeLabel}</div>
                            <div className="text-xs text-slate-400">{slotItems.length} appt</div>
                          </div>
                          {slotItems.length === 0 ? (
                            <p className="text-xs text-slate-300 mt-2">No appointments</p>
                          ) : (
                            <div className="mt-2 space-y-1">
                              {slotItems.map(appt => {
                                const patient = patients.find(p => p.id === appt.patientId);
                                const dentist = dentists.find(d => d.id === appt.providerId);
                                const status = APPT_STATUS[appt.status];
                                return (
                                  <button
                                    key={appt.id}
                                    onClick={() => onEditAppointment(appt)}
                                    className="w-full text-left text-xs px-3 py-2 rounded-xl bg-white border hover:border-blue-200 hover:bg-blue-50 transition-all"
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="font-bold">{patient?.name || 'Unknown'}</span>
                                      <span className="text-[10px] px-2 py-1 rounded-lg font-bold text-slate-600 bg-slate-100">
                                        {status.label}
                                      </span>
                                    </div>
                                    <div className="text-[11px] text-slate-500">
                                      {appt.time} - {appt.type} - {dentist?.name}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {(layout === 'list' || layout === 'grid') && (
                <div className="flex items-center justify-between pt-4 text-sm text-slate-600">
                  <span>
                    Showing {(page - 1) * pageSize + 1}-
                    {Math.min(page * pageSize, filteredAppointments.length)} of {filteredAppointments.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      disabled={page === 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      className="px-3 py-2 rounded-lg border disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span>
                      Page {page} / {totalPages}
                    </span>
                    <button
                      disabled={page === totalPages}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      className="px-3 py-2 rounded-lg border disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
